import { startZipDownload } from '@/lib/zipDownload';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { PropertyWebsiteButton } from '@/components/PropertyWebsiteButton';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Lock, Unlock, Download, X, ChevronLeft, ChevronRight,
  Home, ImageIcon, Star, User, Building2, MapPin, Camera, CreditCard,
  Pencil,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function PropertyGallery() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { isAuthenticated, user, loading: authLoading } = useAuth();

  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [downloadingPhoto, setDownloadingPhoto] = useState(null);
  const [reviewSettings, setReviewSettings] = useState({ google_review_link: '', review_enabled: false });

  // Check for payment success from URL
  const paymentSuccess = searchParams.get('paid') === 'true';

  // Fetch gallery - works with or without authentication when using share token
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        let url = `${API_BASE}/api/gallery/property/${slug}`;
        if (token) {
          url += `?token=${token}`;
        }

        // Use regular fetch for share token access, authFetch for authenticated
        let response;
        if (token && !isAuthenticated) {
          // Share token access - don't redirect on 401
          response = await fetch(url);
        } else {
          // Normal authenticated access - but don't redirect to login
          response = await authFetch(url, {}, false);
        }

        if (!response.ok) {
          if (response.status === 403) {
            // If not authenticated and no valid token, suggest login
            if (!isAuthenticated && !token) {
              // Save return URL for after login
              const returnUrl = `/property/${slug}${token ? `?token=${token}` : ''}`;
              navigate(`/login?returnTo=${encodeURIComponent(returnUrl)}`);
              return;
            }
            throw new Error('Access denied. Please check your share link or sign in.');
          }
          if (response.status === 404) {
            throw new Error('Gallery not found.');
          }
          throw new Error('Failed to load gallery');
        }

        const data = await response.json();
        setGallery(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchGallery();
    }
  }, [slug, token, authLoading, isAuthenticated, navigate]);

  // Fetch invoice - works with share token (no auth required)
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!gallery?.order_id) return;
      
      try {
        // Use the gallery-based invoice endpoint that accepts share token
        let url = `${API_BASE}/api/invoices/gallery/${slug}`;
        if (token) {
          url += `?token=${token}`;
        }
        
        let response;
        if (token && !isAuthenticated) {
          response = await fetch(url);
        } else {
          response = await authFetch(url, {}, false);
        }
        
        if (response.ok) {
          const data = await response.json();
          setInvoice(data);
        }
      } catch (err) {
        console.error('Failed to fetch invoice:', err);
      }
    };

    fetchInvoice();
  }, [gallery?.order_id, slug, token, isAuthenticated]);

  // Fetch review settings
  useEffect(() => {
    const fetchReviewSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/settings/review-link`);
        if (response.ok) {
          const data = await response.json();
          setReviewSettings(data);
        }
      } catch (err) {
        console.error('Failed to fetch review settings:', err);
      }
    };
    fetchReviewSettings();
  }, []);

  // Handle pay invoice - works with share token
  const handlePayInvoice = async () => {
    if (!invoice || invoice.status === 'paid') return;
    
    setPaymentLoading(true);
    try {
      // Use the gallery-based checkout endpoint that accepts share token
      let url = `${API_BASE}/api/invoices/gallery/${slug}/checkout`;
      if (token) {
        url += `?token=${token}`;
      }
      
      let response;
      if (token && !isAuthenticated) {
        response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        response = await authFetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }, false);
      }
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create checkout');
      }
      
      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentLoading(false);
    }
  };

  // Download all photos as one ZIP streamed by the backend (owner, admin, or paid share-token link)
  const handleDownloadAll = async () => {
    if (!gallery?.can_download) return;
    setDownloading(true);
    const url = `${API_BASE}/api/gallery/property/${slug}/zip-ticket${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    await startZipDownload(() =>
      token && !isAuthenticated ? fetch(url, { method: 'POST' }) : authFetch(url, { method: 'POST' }, false)
    );
    setDownloading(false);
  };

  // Download single photo
  const handleDownloadPhoto = async (photo, e) => {
    if (e) e.stopPropagation();
    if (!photo.full_url) return;
    
    setDownloadingPhoto(photo.id);
    try {
      const link = document.createElement('a');
      link.href = photo.full_url;
      link.download = photo.filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setTimeout(() => setDownloadingPhoto(null), 500);
    }
  };

  // Lightbox navigation
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  }, []);

  const prevPhoto = useCallback(() => {
    if (!gallery?.photos) return;
    setLightboxIndex((prev) => (prev === 0 ? gallery.photos.length - 1 : prev - 1));
  }, [gallery?.photos]);

  const nextPhoto = useCallback(() => {
    if (!gallery?.photos) return;
    setLightboxIndex((prev) => (prev === gallery.photos.length - 1 ? 0 : prev + 1));
  }, [gallery?.photos]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevPhoto();
      if (e.key === 'ArrowRight') nextPhoto();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, closeLightbox, prevPhoto, nextPhoto]);

  // Touch/swipe handling for lightbox
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextPhoto();
      else prevPhoto();
    }
  };

  // Loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#C6A14A] animate-spin mx-auto mb-4" />
          <p className="text-[#E8E4DE]/60 text-sm">Loading gallery...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center px-4 text-center">
        <Lock className="h-16 w-16 text-[#E8E4DE]/20 mb-6" />
        <h1 className="text-3xl font-light text-white mb-3">Gallery Unavailable</h1>
        <p className="text-[#E8E4DE]/60 mb-8 max-w-md">{error}</p>
        {!isAuthenticated && !token && (
          <Button asChild className="bg-[#C6A14A] text-[#0a0a0f] hover:bg-[#D4AF5A] px-8">
            <Link to={`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
              Sign In
            </Link>
          </Button>
        )}
      </div>
    );
  }

  if (!gallery) return null;

  const { photos, is_paid, can_download, property_address, share_token_valid, agent_name, brokerage, is_admin, is_owner, shoot_date } = gallery;
  const coverPhoto = photos.find(p => p.is_cover) || photos[0];

  // Only admins can see admin controls - not owners, not share link holders
  const showAdminControls = is_admin;

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Hero Section with Cover Photo */}
      {coverPhoto && (
        <div className="relative w-full h-[50vh] md:h-[60vh] lg:h-[70vh] overflow-hidden">
          {/* Cover Image */}
          <img
            src={is_paid && coverPhoto.full_url ? coverPhoto.full_url : coverPhoto.preview_url || coverPhoto.thumbnail_url}
            alt={property_address || 'Property'}
            className="w-full h-full object-cover"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/40 to-transparent" />
          
          {/* Hero Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 lg:p-16">
            <div className="max-w-7xl mx-auto">
              {/* Status Badge */}
              <div className="flex items-center gap-3 mb-4">
                <Badge className={`${is_paid
                  ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                  : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                } backdrop-blur-sm px-3 py-1`}>
                  {is_paid ? (
                    <><Unlock className="h-3 w-3 mr-1.5" /> Full Access</>
                  ) : (
                    <><Lock className="h-3 w-3 mr-1.5" /> Preview</>
                  )}
                </Badge>
                {share_token_valid && !is_owner && !is_admin && (
                  <Badge variant="outline" className="border-[#C6A14A]/40 text-[#C6A14A] backdrop-blur-sm px-3 py-1">
                    Shared Link
                  </Badge>
                )}
              </div>
              
              {/* Address */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-wide mb-2">
                {property_address || 'Property Gallery'}
              </h1>
              
              {/* Photographed by RC Studios + shoot date */}
              <p className="text-[#E8E4DE]/60 text-sm mb-4">
                Photographed by RC Studios{shoot_date && ` • ${shoot_date}`}
              </p>
              
              {/* Meta Info Row - Only show agent/brokerage to owners/admins */}
              <div className="flex flex-wrap items-center gap-4 md:gap-6 text-[#E8E4DE]/70">
                {agent_name && (is_owner || is_admin) && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-[#C6A14A]" />
                    <span className="text-sm">{agent_name}</span>
                  </div>
                )}
                {brokerage && (is_owner || is_admin) && (
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-[#C6A14A]" />
                    <span className="text-sm">{brokerage}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-[#C6A14A]" />
                  <span className="text-sm">{photos.length} {photos.length === 1 ? 'Photo' : 'Photos'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Click to view in lightbox */}
          {photos.length > 0 && (
            <button
              onClick={() => openLightbox(photos.indexOf(coverPhoto))}
              className="absolute inset-0 cursor-pointer group"
              data-testid="hero-lightbox-trigger"
            >
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  View Full Screen
                </div>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Action Bar */}
      <div className="sticky top-0 z-40 bg-[#0a0a0f]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-[#E8E4DE]/60 hover:text-[#C6A14A] transition-colors">
                <Home className="h-5 w-5" />
              </Link>
              <span className="text-[#E8E4DE]/30">/</span>
              <span className="text-[#E8E4DE]/60 text-sm truncate max-w-[90px] sm:max-w-[200px] md:max-w-none">
                {property_address || 'Gallery'}
              </span>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <PropertyWebsiteButton gallerySlug={slug} className="!h-11 !px-4" />
              {(is_owner || is_admin) && (
                <Link to={`/listing-editor/${slug}`} className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg border border-white/15 text-[#E8E4DE] text-sm hover:border-[#C6A14A]/60 hover:text-[#C6A14A] transition-colors whitespace-nowrap" aria-label="Edit listing" data-testid="gallery-edit-listing-btn">
                  <Pencil className="h-4 w-4" /><span className="hidden sm:inline">Edit listing</span>
                </Link>
              )}
              {/* Google Review Button */}
              {is_paid && reviewSettings.review_enabled && reviewSettings.google_review_link && (
                <a
                  href={reviewSettings.google_review_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:flex items-center gap-2 text-[#C6A14A] hover:text-[#D4AF5A] transition-colors text-sm"
                  data-testid="gallery-review-link"
                >
                  <Star className="h-4 w-4" />
                  Leave a Review
                </a>
              )}

              {/* Download All Button */}
              {can_download && (
                <Button
                  onClick={handleDownloadAll}
                  disabled={downloading}
                  className="bg-[#C6A14A] text-[#0a0a0f] hover:bg-[#D4AF5A] font-medium"
                  data-testid="download-all-btn"
                >
                  {downloading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  <span className="hidden sm:inline">{downloading ? 'Preparing your photos…' : 'Download All (ZIP)'}</span>
                  <span className="sm:hidden">{downloading ? 'Preparing…' : 'All'}</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Success Banner */}
      {paymentSuccess && is_paid && (
        <div className="bg-emerald-500/10 border-b border-emerald-500/20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-full">
                  <Unlock className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-emerald-400 font-medium">Payment Successful!</p>
                  <p className="text-emerald-400/70 text-sm">
                    Full resolution photos are now available for download.
                  </p>
                </div>
              </div>
              {reviewSettings.review_enabled && reviewSettings.google_review_link && (
                <a
                  href={reviewSettings.google_review_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#C6A14A] text-[#0a0a0f] px-5 py-2.5 rounded-lg hover:bg-[#D4AF5A] transition-colors font-medium text-sm whitespace-nowrap"
                  data-testid="gallery-google-review-btn"
                >
                  <Star className="h-4 w-4" />
                  Leave a Review
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Locked Banner with Invoice/Pay Button - Show to share link holders too */}
      {!is_paid && (
        <div className="bg-amber-500/10 border-b border-amber-500/20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-500/20 rounded-full">
                  <Lock className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-amber-400 font-medium text-lg">Preview Mode</p>
                  <p className="text-amber-400/70 text-sm">
                    {photos.length} watermarked {photos.length === 1 ? 'photo' : 'photos'} • Full resolution available after payment
                  </p>
                </div>
              </div>
              
              {/* Pay Invoice button - LARGER with photo count */}
              {invoice && invoice.status !== 'paid' && invoice.status !== 'draft' && (
                <Button
                  size="lg"
                  className="bg-[#C6A14A] hover:bg-[#d4af5a] text-[#0a0a0f] font-bold whitespace-nowrap px-8 py-6 text-base shadow-lg shadow-[#C6A14A]/20"
                  onClick={handlePayInvoice}
                  disabled={paymentLoading}
                  data-testid="gallery-pay-invoice-btn"
                >
                  {paymentLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Unlock className="h-5 w-5 mr-2" />
                  )}
                  {photos.length > 0
                    ? `Unlock ${photos.length} Full-Resolution ${photos.length === 1 ? 'Photo' : 'Photos'}`
                    : 'Pay Invoice'} • ${(invoice.total_cents / 100).toFixed(2)}
                </Button>
              )}
              
              {invoice && invoice.status === 'draft' && (
                <Badge variant="outline" className="border-amber-500/30 text-amber-400 px-4 py-2">
                  Invoice Pending
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Grid - Masonry Style */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="p-6 bg-white/5 rounded-full mb-6">
              <ImageIcon className="h-12 w-12 text-[#E8E4DE]/30" />
            </div>
            <h3 className="text-xl text-white mb-2">No Photos Yet</h3>
            <p className="text-[#E8E4DE]/60 max-w-sm">
              Photos will appear here once they're uploaded.
            </p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                onClick={() => openLightbox(index)}
                className="relative break-inside-avoid cursor-pointer group rounded-lg overflow-hidden bg-[#12121a]"
                data-testid={`gallery-photo-${photo.id}`}
              >
                <img
                  src={is_paid && photo.full_url ? photo.full_url : photo.preview_url || photo.thumbnail_url}
                  alt={photo.filename}
                  className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {/* Watermark notice for locked photos */}
                  {!is_paid && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg text-center">
                        <Lock className="h-5 w-5 text-white/80 mx-auto mb-1" />
                        <span className="text-white/80 text-xs">Watermarked</span>
                      </div>
                    </div>
                  )}

                  {/* Download button for unlocked photos */}
                  {is_paid && photo.full_url && (
                    <button
                      onClick={(e) => handleDownloadPhoto(photo, e)}
                      disabled={downloadingPhoto === photo.id}
                      className="absolute bottom-3 right-3 p-2.5 bg-[#C6A14A] rounded-lg transition-all hover:bg-[#D4AF5A] disabled:opacity-50"
                      data-testid={`download-photo-${photo.id}`}
                    >
                      {downloadingPhoto === photo.id ? (
                        <Loader2 className="h-4 w-4 text-[#0a0a0f] animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 text-[#0a0a0f]" />
                      )}
                    </button>
                  )}
                </div>

                {/* Cover badge - only show to admins */}
                {photo.is_cover && is_admin && (
                  <div className="absolute top-3 left-3">
                    <Badge className="bg-[#C6A14A] text-[#0a0a0f] text-xs font-medium">Cover</Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && gallery.photos.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-3 text-white/60 hover:text-white z-10 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            data-testid="lightbox-close"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-4 text-white/60 text-sm bg-white/10 px-4 py-2 rounded-full">
            {lightboxIndex + 1} / {photos.length}
          </div>

          {/* Navigation - Previous */}
          <button
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/60 hover:text-white z-10 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          
          {/* Navigation - Next */}
          <button
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/60 hover:text-white z-10 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight className="h-8 w-8" />
          </button>

          {/* Image */}
          <div
            className="max-w-[95vw] max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={
                is_paid && photos[lightboxIndex]?.full_url
                  ? photos[lightboxIndex].full_url
                  : photos[lightboxIndex]?.preview_url
              }
              alt={photos[lightboxIndex]?.filename}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>

          {/* Bottom bar */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <span className="text-white/60 text-sm truncate max-w-[50%]">
                {photos[lightboxIndex]?.filename}
              </span>
              {is_paid && photos[lightboxIndex]?.full_url && (
                <button
                  onClick={(e) => handleDownloadPhoto(photos[lightboxIndex], e)}
                  disabled={downloadingPhoto === photos[lightboxIndex]?.id}
                  className="flex items-center gap-2 bg-[#C6A14A] text-[#0a0a0f] px-4 py-2 rounded-lg hover:bg-[#D4AF5A] transition-colors font-medium text-sm"
                  data-testid="lightbox-download"
                >
                  {downloadingPhoto === photos[lightboxIndex]?.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Download
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
